import type { Dispatch, SetStateAction } from "react";
import React from "react";
import { type FieldErrors, type UseFormRegister } from "react-hook-form";
import { TextField } from "@c4c-web/ui";
import type {
  IUseAddressFormSetters,
  IUseAddressFormStates,
} from "../../../hooks";
import type { IAddress, ILocation, TranslationFn } from "../../../types";
import type { EStoreIds } from "../../../constants";
import { getFormFieldRequirements } from "../../../utils";
import type { ICity } from "./address-form.interface";
import { DefaultFormFields } from "./DefaultFormFields";
import { KuwaitFormFields } from "./KuwaitFormFields";
import { AreaFormField } from "./AreaFormField";
import { QatarFormFields } from "./QatarFormFields";

export interface IFormFieldsProps {
  addressFormSetters?: IUseAddressFormSetters;
  addressFormStates?: IUseAddressFormStates;
  editInfo?: {
    id?: string;
    coordinates?: ILocation;
  };
  errors: FieldErrors<IAddress>;
  formData?: IAddress;
  handleMapAddress?: (value: IAddress) => void;
  register: UseFormRegister<IAddress>;
  setIsSelected?: (value: boolean) => void;
  setIsTyping?: (value: boolean) => void;
  setShowMap?: Dispatch<SetStateAction<boolean>>;
  t: TranslationFn;
  toggleForm?: (value: boolean) => void;
}

export interface IAddressFormFieldsProps extends IFormFieldsProps {
  address: IAddress;
  addressFormSetters: IUseAddressFormSetters;
  addressFormStates: IUseAddressFormStates;
  cities: ICity[];
  storeId: EStoreIds;
}

export const AddressFormFields: React.FC<IAddressFormFieldsProps> = ({
  address,
  addressFormSetters,
  addressFormStates,
  cities,
  editInfo,
  errors,
  formData,
  handleMapAddress,
  register,
  storeId,
  setIsSelected,
  setIsTyping,
  setShowMap,
  t,
  toggleForm,
}) => {
  const {
    isDefaultFormFieldRequired,
    isKuwaitFormFieldRequired,
    isAreaFormFieldRequired,
    isQatarFormFieldRequired,
  } = getFormFieldRequirements(storeId);

  return (
    <div className="flex flex-col gap-sm">
      {isDefaultFormFieldRequired ? (
        <DefaultFormFields
          addressFormStates={addressFormStates}
          errors={errors}
          formData={formData}
          register={register}
          t={t}
        />
      ) : null}

      {isKuwaitFormFieldRequired ? (
        <KuwaitFormFields
          addressFormStates={addressFormStates}
          errors={errors}
          formData={formData}
          register={register}
          t={t}
        />
      ) : null}

      {isAreaFormFieldRequired ? (
        <AreaFormField
          address={address}
          editInfo={editInfo}
          errors={errors}
          handleMapAddress={handleMapAddress}
          register={register}
          setIsSelected={setIsSelected}
          setIsTyping={setIsTyping}
          setShowMap={setShowMap}
          t={t}
          toggleForm={toggleForm}
        />
      ) : null}

      {isQatarFormFieldRequired ? (
        <QatarFormFields
          addressFormSetters={addressFormSetters}
          addressFormStates={addressFormStates}
          cities={cities}
          errors={errors}
          formData={formData}
          register={register}
          t={t}
        />
      ) : null}

      <TextField
        defaultValue={formData?.landmark}
        haveBoldText
        id="landmark"
        label={t("addressJourney.addressForm.landmark")}
        {...register("landmark")}
        placeholder={t("addressJourney.addressForm.placeHolders.landmark")}
      />
    </div>
  );
};
